/**
 * Additional parameters that can be provided by specific packages, on top of regular theme state.
 */
export type ThemeProps = {
    /**
     * Logo to be rendered inside of the flow card
     */
    cardLogo?: JSX.Element;
};

/**
 * Defines base type for all flow props that adds frontend-specific parameters to
 * TProps that are page properties provided by the backend.
 */
export type FlowProps<PageProps> = PageProps & ThemeProps;

/**
 * @param {any} locationState comes from react-router useLocation().state - redirect state after SPA navigate (does not have theme props)
 * @param {FlowProps} flowArgs comes from page parameters, i.e. whatever was serialized on page, plus theme props provided by the app package
 * @returns {FlowProps} reconciled state to be passed to the page component
 */
export function combineProps<TProps>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    locationState: any,
    flowArgs: FlowProps<TProps>,
): FlowProps<TProps> {
    // if we have not navigated SPA state, this is just a regular page render
    if (!locationState) return flowArgs;

    // if we are navigating with the router state, state is missing theme props,
    // so take them from the page
    const themeProps = pick<FlowProps<TProps>, keyof ThemeProps>(
        flowArgs,
        'cardLogo',
    );

    const combined = { ...themeProps, ...(locationState as TProps) };
    return combined;
}

// https://stackoverflow.com/questions/17781472/how-to-get-a-subset-of-a-javascript-objects-properties
// eslint-disable-next-line @typescript-eslint/ban-types
const pick = <T extends {}, K extends keyof T>(obj: T, ...keys: K[]) =>
    Object.fromEntries(
        keys.filter((key) => key in obj).map((key) => [key, obj[key]]),
    ) as Pick<T, K>;
